import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DocumentsAPI } from '@/services/documentsAPI';
import { Document, DocumentFormData } from '@/types/documents';
import Page from '@/components/Page';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import Chip from '@/components/Chip';
import LoadingState from '@/components/LoadingState';
import ROUTES from '@/routes/routes';
import PdfSignaturePlacer from './PdfSignaturePlacer';

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const DocumentForm: React.FC = () => {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const isEdit = !!documentId;

  // Step state for create wizard (1 = details, 2 = signature placement)
  const [step, setStep] = useState<1 | 2>(1);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [formData, setFormData] = useState<DocumentFormData>({
    name: '',
    tax_type: 'all',
    states: ['ALL'],
  });
  const [allStates, setAllStates] = useState(true);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [pdfFileName, setPdfFileName] = useState<string>('');

  // Signature position state
  const [currentDoc, setCurrentDoc] = useState<Document | null>(null);
  const [sigPage, setSigPage] = useState<string>('1');
  const [sigX, setSigX] = useState<string>('');
  const [sigY, setSigY] = useState<string>('');
  const [savingSignature, setSavingSignature] = useState(false);
  const [signatureSuccess, setSignatureSuccess] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && documentId) {
      fetchDocument();
    }
  }, [isEdit, documentId]);

  const fetchDocument = async () => {
    if (!documentId) return;
    setInitialLoading(true);
    setError(null);
    try {
      const response = await DocumentsAPI.getDocuments();
      if (response.success) {
        const doc = response.data.find((d) => d.id === Number(documentId));
        if (doc) {
          setCurrentDoc(doc);
          setFormData({
            name: doc.name,
            tax_type: doc.tax_type,
            states: doc.states || ['ALL'],
          });
          const isAll = doc.states?.includes('ALL') || doc.states?.length === 0;
          setAllStates(isAll);
          if (!isAll) {
            setSelectedStates(doc.states || []);
          }
          // Pre-fill signature fields from latest version
          if (doc.latest_version?.has_signature_coordinates) {
            setSigPage(String(doc.latest_version.signature_page || 1));
            setSigX(String(doc.latest_version.signature_x ?? ''));
            setSigY(String(doc.latest_version.signature_y ?? ''));
          }
        }
      }
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to load document',
      );
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAllStatesToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAllStates(checked);
    if (checked) {
      setFormData((prev) => ({ ...prev, states: ['ALL'] }));
      setSelectedStates([]);
    } else {
      setFormData((prev) => ({ ...prev, states: [] }));
    }
  };

  const handleStateToggle = (state: string) => {
    setSelectedStates((prev) => {
      const newStates = prev.includes(state)
        ? prev.filter((s) => s !== state)
        : [...prev, state];
      setFormData((fd) => ({ ...fd, states: newStates }));
      return newStates;
    });
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPdf(true);
    setError(null);

    try {
      const uploadData = await DocumentsAPI.getUploadUrl(file.name, file.type);

      const uploadResponse = await fetch(uploadData.data.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `S3 upload failed with status ${uploadResponse.status}`,
        );
      }

      setFormData((prev) => ({ ...prev, s3_key: uploadData.data.s3_key }));
      setPdfFileName(file.name);
    } catch (err: any) {
      setError('Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!isEdit && !formData.s3_key) {
        setError('Please upload a PDF file');
        setLoading(false);
        return;
      }

      if (!allStates && selectedStates.length === 0) {
        setError('Please select at least one state or check "All States"');
        setLoading(false);
        return;
      }

      if (isEdit && documentId) {
        const { s3_key, ...updateData } = formData;
        await DocumentsAPI.updateDocument(Number(documentId), updateData);
        navigate(`/${ROUTES.documents}`);
      } else {
        // Create mode: after creating, move to step 2 for signature placement
        const response = await DocumentsAPI.createDocument(formData);
        if (response.success && response.data) {
          setCurrentDoc(response.data);
          setStep(2);
        } else {
          navigate(`/${ROUTES.documents}`);
        }
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to save document';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSignature = async () => {
    if (!currentDoc?.latest_version) return;

    const pageNum = parseInt(sigPage, 10);
    const x = parseFloat(sigX);
    const y = parseFloat(sigY);

    if (!pageNum || pageNum < 1) {
      setSignatureError('Page number must be at least 1');
      return;
    }
    if (isNaN(x) || x < 0) {
      setSignatureError('X position must be a valid non-negative number');
      return;
    }
    if (isNaN(y) || y < 0) {
      setSignatureError('Y position must be a valid non-negative number');
      return;
    }

    setSavingSignature(true);
    setSignatureError(null);

    try {
      await DocumentsAPI.updateVersion(
        currentDoc.id,
        currentDoc.latest_version.id,
        { signature_page: pageNum, signature_x: x, signature_y: y },
      );

      if (!isEdit && step === 2) {
        // Create wizard: after saving signature, go to document list
        navigate(`/${ROUTES.documents}`);
      } else {
        // Edit mode: stay on page, show success
        setSignatureSuccess('Signature position saved successfully');
        await fetchDocument();
        setTimeout(() => setSignatureSuccess(null), 3000);
      }
    } catch (err: any) {
      setSignatureError(
        err.response?.data?.errors?.join(', ') ||
          'Failed to save signature position',
      );
    } finally {
      setSavingSignature(false);
    }
  };

  const handleSkipSignature = () => {
    navigate(`/${ROUTES.documents}`);
  };

  const handleCancel = () => {
    navigate(`/${ROUTES.documents}`);
  };

  if (initialLoading) {
    return (
      <Page title={isEdit ? 'Edit Document' : 'Create Document'}>
        <Card>
          <LoadingState />
        </Card>
      </Page>
    );
  }

  const latestVersion = currentDoc?.latest_version;
  const showSignatureSection = isEdit && latestVersion;
  // In create mode step 2, use the just-created document
  const createStepTwoVersion = !isEdit && step === 2 ? latestVersion : null;

  const pageTitle = isEdit
    ? 'Edit Document'
    : step === 1
      ? 'Create New Document'
      : 'Set Signature Position';

  return (
    <Page
      breadcrumbs={[
        {
          label: 'Documents',
          to: `/${ROUTES.documents}`,
        },
      ]}
      size="md"
      title={pageTitle}
    >
      {/* Step indicator for create mode */}
      {!isEdit && (
        <div style={{ marginBottom: '24px' }}>
          <Stack gap={0}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: step >= 1 ? '#2563eb' : '#ddd',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                {step > 1 ? '\u2713' : '1'}
              </div>
              <Body weight={step === 1 ? 'medium' : 'regular'}>
                Document Details
              </Body>
            </div>
            <div
              style={{
                width: '40px',
                height: '2px',
                background: step >= 2 ? '#2563eb' : '#ddd',
                alignSelf: 'center',
              }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: step >= 2 ? '#2563eb' : '#ddd',
                  color: step >= 2 ? '#fff' : '#999',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                2
              </div>
              <Body weight={step === 2 ? 'medium' : 'regular'}>
                Signature Placement
              </Body>
            </div>
          </Stack>
        </div>
      )}

      {/* Step 1: Document details (create) or Edit form */}
      {(isEdit || step === 1) && (
        <form onSubmit={handleSubmit}>
          <Card>
            {error && (
              <Card.Section>
                <Alert description={error} status="danger" title="Error" />
              </Card.Section>
            )}

            <Card.Section>
              <Stack gap={16} vertical>
                <div style={{ width: '100%' }}>
                  <label
                    htmlFor="name"
                    style={{ display: 'block', marginBottom: '4px' }}
                  >
                    <Body weight="medium">Document Name *</Body>
                  </label>
                  <input
                    id="name"
                    name="name"
                    required
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Employee Handbook"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ width: '100%' }}>
                  <label
                    htmlFor="tax_type"
                    style={{ display: 'block', marginBottom: '4px' }}
                  >
                    <Body weight="medium">Tax Type *</Body>
                  </label>
                  <select
                    id="tax_type"
                    name="tax_type"
                    required
                    value={formData.tax_type}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  >
                    <option value="all">All Workers</option>
                    <option value="w2">W-2 Only</option>
                    <option value="1099">1099 Only</option>
                  </select>
                  <Small>Which workers should see this document</Small>
                </div>

                <div style={{ width: '100%' }}>
                  <Body weight="medium">States *</Body>
                  <div style={{ marginTop: '8px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allStates}
                        onChange={handleAllStatesToggle}
                      />
                      <Body>All States</Body>
                    </label>
                  </div>
                  {!allStates && (
                    <div
                      style={{
                        marginTop: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '8px',
                      }}
                    >
                      {US_STATES.map((state) => (
                        <label
                          key={state}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            cursor: 'pointer',
                            minWidth: '60px',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedStates.includes(state)}
                            onChange={() => handleStateToggle(state)}
                          />
                          <Small>{state}</Small>
                        </label>
                      ))}
                    </div>
                  )}
                  <Small>
                    {allStates
                      ? 'This document will be shown to workers in all states'
                      : `Selected: ${selectedStates.length > 0 ? selectedStates.join(', ') : 'None'}`}
                  </Small>
                </div>

                {!isEdit && (
                  <div style={{ width: '100%' }}>
                    <label
                      htmlFor="pdf_file"
                      style={{ display: 'block', marginBottom: '4px' }}
                    >
                      <Body weight="medium">PDF Document *</Body>
                    </label>
                    <input
                      id="pdf_file"
                      name="pdf_file"
                      type="file"
                      accept="application/pdf"
                      onChange={handlePdfUpload}
                      disabled={uploadingPdf}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                      }}
                    />
                    {uploadingPdf && <Small>Uploading PDF...</Small>}
                    {pdfFileName && !uploadingPdf && (
                      <Small>Uploaded: {pdfFileName}</Small>
                    )}
                  </div>
                )}
              </Stack>
            </Card.Section>

            <Card.Section subdued>
              <Stack gap={12} justify="end">
                <Button
                  a11yLabel="Cancel"
                  appearance="outline"
                  label="Cancel"
                  type="button"
                  onClick={handleCancel}
                />
                <Button
                  a11yLabel={
                    isEdit ? 'Update Document' : 'Continue to Signature Setup'
                  }
                  isLoading={loading || uploadingPdf}
                  label={isEdit ? 'Update' : 'Next: Set Signature'}
                  type="submit"
                />
              </Stack>
            </Card.Section>
          </Card>
        </form>
      )}

      {/* Step 2: Signature placement (create wizard) */}
      {!isEdit && step === 2 && createStepTwoVersion && (
        <Card>
          <Card.Header
            title={`Signature Position \u2014 v${createStepTwoVersion.version_number}`}
          />
          <Card.Section>
            {signatureError && (
              <div style={{ marginBottom: '12px' }}>
                <Alert
                  description={signatureError}
                  status="danger"
                  title="Error"
                  onDismiss={() => setSignatureError(null)}
                />
              </div>
            )}

            <Stack gap={16} vertical>
              <Alert
                description="Place the signature box on the PDF where workers should sign. Click anywhere to place or drag the box to reposition it."
                status="info"
                title="Set the signature position"
              />

              {createStepTwoVersion.download_url && (
                <PdfSignaturePlacer
                  pdfUrl={createStepTwoVersion.download_url}
                  initialPage={1}
                  initialX={null}
                  initialY={null}
                  onPositionChange={(page, x, y) => {
                    setSigPage(String(page));
                    setSigX(String(x));
                    setSigY(String(y));
                  }}
                />
              )}
            </Stack>
          </Card.Section>
          <Card.Section subdued>
            <Stack gap={12} justify="end">
              <Button
                a11yLabel="Skip signature setup"
                appearance="outline"
                label="Skip for Now"
                type="button"
                onClick={handleSkipSignature}
              />
              <Button
                a11yLabel="Save signature position and finish"
                isLoading={savingSignature}
                label="Save Position & Finish"
                onClick={handleSaveSignature}
              />
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Signature section for edit mode */}
      {showSignatureSection && (
        <div style={{ marginTop: '24px' }}>
          <Card>
            <Card.Header
              title={`Signature Position \u2014 v${latestVersion.version_number}`}
            />
            <Card.Section>
              {signatureError && (
                <div style={{ marginBottom: '12px' }}>
                  <Alert
                    description={signatureError}
                    status="danger"
                    title="Error"
                    onDismiss={() => setSignatureError(null)}
                  />
                </div>
              )}
              {signatureSuccess && (
                <div style={{ marginBottom: '12px' }}>
                  <Alert
                    description={signatureSuccess}
                    status="success"
                    title="Success"
                  />
                </div>
              )}

              <Stack gap={16} vertical>
                <Stack gap={8}>
                  <Body>Status:</Body>
                  {latestVersion.has_signature_coordinates ? (
                    <Chip color="success" label="Set" size="small" />
                  ) : (
                    <Chip label="Not set" size="small" />
                  )}
                  {latestVersion.has_signature_coordinates && (
                    <Small>
                      Page {latestVersion.signature_page}, X:{' '}
                      {latestVersion.signature_x?.toFixed(1)}, Y:{' '}
                      {latestVersion.signature_y?.toFixed(1)}
                    </Small>
                  )}
                </Stack>
                {!latestVersion.has_signature_coordinates && (
                  <Alert
                    description="This document cannot be activated until a signature position is set. Use the PDF viewer below to place the signature box, then click Save Position."
                    status="warning"
                    title="Signature position required"
                  />
                )}

                {latestVersion.download_url && (
                  <PdfSignaturePlacer
                    pdfUrl={latestVersion.download_url}
                    initialPage={latestVersion.signature_page || 1}
                    initialX={latestVersion.signature_x}
                    initialY={latestVersion.signature_y}
                    onPositionChange={(page, x, y) => {
                      setSigPage(String(page));
                      setSigX(String(x));
                      setSigY(String(y));
                    }}
                  />
                )}
              </Stack>
            </Card.Section>
            <Card.Section subdued>
              <Stack justify="end">
                <Button
                  a11yLabel="Save signature position"
                  isLoading={savingSignature}
                  label="Save Position"
                  onClick={handleSaveSignature}
                />
              </Stack>
            </Card.Section>
          </Card>
        </div>
      )}
    </Page>
  );
};

export default DocumentForm;
